import type { BuyerLeadStatus } from "@/api/buyers";
import { buyerLabel } from "./buyerFilters";
import { buyerStatusTone } from "./buyerListBits";
import { definePhaseLadder, type PhaseDef } from "./phaseLadder";

/**
 * The buyer funnel's ELEVEN statuses (L4), condensed to five chips.
 *
 * <p>The FE twin of <c>BuyerLeadPhases</c> (Core/Common) — keep the keys, the
 * labels and the membership IDENTICAL, and the shared fixture
 * <c>tests/fixtures/buyer-status-vocabulary.json</c> is what enforces it.</p>
 *
 * <p><b>Why condense at all.</b> Eleven statuses is the right vocabulary for
 * MOVING a buyer — every one of them is a distinct thing a specialist decides —
 * and the wrong one for READING a book at a glance. The chips answer "how far
 * along", the badge beside them answers "where exactly", and both always render.
 * The status-change modal still offers the full eleven; nothing about the state
 * machine, the terminal-reason requirement or the milestone stamps changes.</p>
 *
 * <p>🔴 <b>The groupings follow the MILESTONE STAMPS, not intuition.</b> The
 * server stamps <c>FirstContactAttemptAtUtc</c> at AttemptingContact,
 * <c>FirstLiveConversationAtUtc</c> at Engaged and <c>QualifiedAtUtc</c> at
 * Qualified — so those are the boundaries the funnel report already measures,
 * and a chip ladder drawn anywhere else would tell a different story from the
 * numbers.</p>
 */
const PHASES: readonly PhaseDef<BuyerLeadStatus>[] = [
  {
    key: "intake",
    label: "Intake",
    // Arrived and routed, but nobody has tried to reach them yet. New and
    // Assigned share a chip because the difference between them is WHO owns the
    // record rather than how far it has got — and the Assignment Queue, the
    // "unassigned" stat card and the owner column all answer that question
    // better than a chip could.
    statuses: ["New", "Assigned"],
  },
  {
    key: "contact",
    label: "Contact",
    // First attempt through first live conversation — the two contact stamps.
    // Named for the record's own word (FirstContactAttemptAtUtc) rather than
    // "Outreach", which on the SUBMISSION ladder means reaching the listing
    // broker. Two ladders, two targets; the same word on both would be a trap.
    statuses: ["AttemptingContact", "Engaged"],
  },
  {
    key: "qualified",
    label: "Qualified",
    // The five-dimension verdict is in. Its own chip because it is the gate the
    // whole book is sorted on, and burying it inside "Contact" would make the
    // qualified and the merely-spoken-to look alike.
    statuses: ["Qualified"],
  },
  {
    key: "submitting",
    label: "Submitting",
    statuses: ["ActivelySubmitting"],
  },
  {
    key: "won",
    label: "Won",
    // Both good ends. ClosedPurchased ranks above ConvertedToBorrower, but the
    // difference is WHICH win, not how far — and the badge says which.
    statuses: ["ConvertedToBorrower", "ClosedPurchased"],
  },
];

/**
 * The three terminal statuses, which the server requires a reason for.
 *
 * <p>🔴 They are OFF the ladder rather than a sixth chip. Closing a buyer out as
 * Not qualified or Nurture is an ordinary, correct outcome — the same reasoning
 * that makes all three <c>muted</c> rather than <c>danger</c> in
 * <c>buyerStatusTone</c> — and a terminal rung at the END would read as the
 * destination the funnel is pointed at. Nurture is not even an ending; it is a
 * "come back later".</p>
 */
const OFF_LADDER: readonly BuyerLeadStatus[] = ["NotQualified", "Nurture", "Dead"];

/**
 * Which statuses close a buyer out, and so require a reason on the status change
 * (<c>BuyerLeadService.IsTerminal</c>, pinned to the shared fixture by
 * <c>BuyerStatusVocabularyParityTests</c>). Identical to the off-ladder set by
 * construction — the <c>SUBMISSION_TERMINAL_STATUSES</c> precedent.
 */
export const BUYER_TERMINAL_STATUSES: readonly BuyerLeadStatus[] = OFF_LADDER;

function noteFor(status: BuyerLeadStatus): string {
  switch (status) {
    case "Nurture":
      return "Parked, not lost — the buyer rejoins the ladder if they come back.";
    case "NotQualified":
      return "Closed out on qualification. The reason is on the status change.";
    default:
      return "Closed out. The reason is on the status change.";
  }
}

export const buyerPhaseLadder = definePhaseLadder<BuyerLeadStatus>({
  phases: PHASES,
  offLadder: OFF_LADDER,
  labelOf: buyerLabel,
  toneOf: buyerStatusTone,
  offLadderNote: noteFor,
});

/** The chip a buyer's status rolls up to; `null` when it is off the ladder. */
export const buyerPhaseFor = buyerPhaseLadder.phaseFor;

/** Everything a render needs — chips, the current rung, the raw badge. */
export const describeBuyerPhase = buyerPhaseLadder.describe;
